/* eslint-disable */
import React, { useMemo, useState } from "react";
import {
  createStyles,
  Card,
  Group,
  Text,
  Select,
  Badge,
  ThemeIcon,
  Center,
  Loader,
} from "@mantine/core";
import * as Recharts from "recharts";
import { IconCalendarStats } from "@tabler/icons-react";
import { useGetLeaveTypeSummary } from "api/leaveReport/getleaveSummaryReportYearly";

const ResponsiveContainerAny =
  Recharts.ResponsiveContainer as unknown as React.ComponentType<any>;
const BarChartAny = Recharts.BarChart as unknown as React.ComponentType<any>;
const BarAny = Recharts.Bar as unknown as React.ComponentType<any>;
const XAxisAny = Recharts.XAxis as unknown as React.ComponentType<any>;
const YAxisAny = Recharts.YAxis as unknown as React.ComponentType<any>;
const CartesianGridAny =
  Recharts.CartesianGrid as unknown as React.ComponentType<any>;
const TooltipAny = Recharts.Tooltip as unknown as React.ComponentType<any>;
// Removed LegendAny ❌

const useStyles = createStyles((theme) => ({
  card: {
    height: "100%",
    padding: theme.spacing.md,
    borderRadius: theme.radius.md,
    border: `1px solid ${theme.colors.gray[2]}`,
    background: theme.white,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing.sm,
  },
  left: { display: "flex", alignItems: "center", gap: theme.spacing.md },
  title: { display: "flex", alignItems: "center", gap: 8, fontWeight: 700 },
  legend: { display: "flex", gap: 12, alignItems: "center" },
  legendItem: { display: "flex", gap: 6, alignItems: "center" },
  chartWrap: { height: 250 }, // reduced height
  pillSelect: { width: 140 },
}));

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const FULL_TO_SHORT: Record<string, string> = {
  january: "Jan",
  february: "Feb",
  march: "Mar",
  april: "Apr",
  may: "May",
  june: "Jun",
  july: "Jul",
  august: "Aug",
  september: "Sep",
  october: "Oct",
  november: "Nov",
  december: "Dec",
};

function normalizeMonthToShort(m: string | undefined) {
  if (!m) return "";
  const t = m.trim().toLowerCase();
  if (FULL_TO_SHORT[t]) return FULL_TO_SHORT[t];
  return m.slice(0, 3).replace(/^\w/, (c) => c.toUpperCase());
}

export default function LeavesStackedChart({ department, employee }: any) {
  const { classes, theme } = useStyles();

  const currentYear = new Date().getFullYear();
  const [yearOption, setYearOption] = useState<"thisYear" | "lastYear">(
    "thisYear"
  );
  const selectedYear =
    yearOption === "thisYear" ? currentYear : currentYear - 1;

  const {
    data: apiResponse,
    isLoading,
    isError,
  } = useGetLeaveTypeSummary(selectedYear, department, employee);

  const colors = {
    annual: theme.colors.green?.[6] || "#2ecc71",
    casual: theme.colors.yellow?.[6] || "#f1c40f",
    medical: theme.colors.blue?.[6] || "#0b3d91",
    others: theme.colors.orange?.[6] || "#ff7043",
  };

  const chartData = useMemo(() => {
    const apiItems: any[] = apiResponse?.data ?? [];
    const mapByMonth = new Map<string, any>();
    apiItems.forEach((it) => {
      const short = normalizeMonthToShort(it.month);
      mapByMonth.set(short, it);
    });

    return MONTHS_SHORT.map((m) => {
      const found = mapByMonth.get(m);
      return {
        month: m,
        annual: Number(found?.annual ?? 0),
        casual: Number(found?.casual ?? 0),
        medical: Number(found?.medical ?? 0),
        others: Number(found?.others ?? 0),
      };
    });
  }, [apiResponse]);

  return (
    <Card className={classes.card} radius="md">
      {/* --- Header --- */}
      <div className={classes.header}>
        <div className={classes.left}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <ThemeIcon
              radius="xl"
              size={28}
              variant="filled"
              style={{ background: theme.colors.cyan[6], color: "white" }}
            >
              <IconCalendarStats size={16} />
            </ThemeIcon>
            <Text size="sm" fw={700}>
              Leaves
            </Text>
          </div>

          <div className={classes.legend}>
            {Object.entries(colors).map(([key, value]) => (
              <div className={classes.legendItem} key={key}>
                <Badge
                  radius="sm"
                  style={{
                    backgroundColor: value,
                    width: 12,
                    height: 12,
                  }}
                />
                <Text size="sm" c="black" tt="capitalize">
                  {key}
                </Text>
              </div>
            ))}
          </div>
        </div>

        <Select
          value={yearOption}
          onChange={(v) => setYearOption((v as any) ?? "thisYear")}
          data={[
            { value: "thisYear", label: `This Year (${currentYear})` },
            { value: "lastYear", label: `Last Year (${currentYear - 1})` },
          ]}
          className={classes.pillSelect}
        />
      </div>

      {/* --- Chart --- */}
      <div className={classes.chartWrap}>
        {isLoading ? (
          <Center style={{ height: "100%" }}>
            <Loader />
          </Center>
        ) : isError ? (
          <Center style={{ height: "100%" }}>
            <Text color="red">Failed to load data.</Text>
          </Center>
        ) : (
          <ResponsiveContainerAny width="100%" height="100%">
            <BarChartAny
              data={chartData}
              margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
            >
              <CartesianGridAny strokeDasharray="3 3" vertical={false} />

              <XAxisAny
                dataKey="month"
                label={{
                  value: "Months",
                  position: "bottom",
                  offset: 0,
                }}
              />

              <YAxisAny
                label={{
                  value: "Leave Count",
                  angle: -90,
                  position: "insideLeft",
                  offset: 10,
                }}
              />

              <TooltipAny />

              <BarAny dataKey="annual" stackId="a" fill={colors.annual} />
              <BarAny dataKey="casual" stackId="a" fill={colors.casual} />
              <BarAny dataKey="medical" stackId="a" fill={colors.medical} />
              <BarAny dataKey="others" stackId="a" fill={colors.others} />
            </BarChartAny>
          </ResponsiveContainerAny>
        )}
      </div>
    </Card>
  );
}
